import React from "react";
import {
  Container,
  Overline,
  Reveal,
  CourseHeader,
  CourseFooter,
  CardShell,
  BulletList,
  Faq,
  useSeo,
  ArrowRight,
  Sparkles,
  CircleDot,
} from "@/components/coursePage/shared";
import {
  Mail,
  FileText,
  Table2,
  Presentation,
  Users,
  Zap,
  Award,
  GraduationCap,
  Sparkles as SparklesIcon,
} from "lucide-react";

const COPILOT_URL = "https://justin-s-school217.teachable.com/p/copilotquickstart";

const BENEFITS = [
  { icon: Zap, title: "Work Faster", desc: "Use Copilot to reduce time spent drafting, organizing, and completing routine tasks." },
  { icon: Mail, title: "Communicate Better", desc: "Create clearer emails, reports, summaries, and professional documents." },
  { icon: Table2, title: "Understand Your Data", desc: "Use Copilot to explore spreadsheets, formulas, trends, and business insights." },
  { icon: Presentation, title: "Build Better Workflows", desc: "Combine Copilot with Microsoft tools to create a more productive daily routine." },
];

const AUDIENCE = [
  "Professionals using Microsoft 365",
  "Managers and team leaders",
  "Small-business owners",
  "Students and job seekers",
  "Beginners exploring workplace AI",
  "Anyone wanting to save time with Copilot",
];

const CURRICULUM = [
  {
    stage: "Welcome to Microsoft Copilot",
    icon: SparklesIcon,
    lessons: [
      "Welcome & Course Overview",
      "How to Get the Most from This Course",
      "Setting Up Microsoft Copilot",
      "What You'll Learn",
    ],
  },
  {
    stage: "The 7-Day Microsoft Copilot QuickStart",
    icon: GraduationCap,
    lessons: [
      "Day 1: Introduction to Microsoft Copilot",
      "Day 2: Writing with Copilot",
      "Day 3: Microsoft Word & Outlook",
      "Day 4: Microsoft Excel",
      "Day 5: Microsoft PowerPoint",
      "Day 6: Microsoft Teams & Productivity",
      "Day 7: Building Your AI Workflow",
    ],
  },
  {
    stage: "Graduation & Next Steps",
    icon: Award,
    lessons: [
      "Congratulations",
      "Certificate of Completion",
      "What's Next at JK Academy",
      "Continue Your AI Journey",
    ],
  },
];

const STUDENT_RECEIVES = [
  "Complete self-paced course",
  "Seven focused daily lessons",
  "Practical prompts and examples",
  "Microsoft 365 productivity guidance",
  "Ongoing access through your JK Academy student account",
  "JK Academy certificate of completion",
];

const FAQ = [
  {
    q: "Do I need previous AI experience?",
    a: "No. The course is designed for beginners and introduces Copilot one step at a time.",
  },
  {
    q: "Do I need Microsoft 365?",
    a: "Students can learn core Copilot concepts using available Copilot access. Certain demonstrations involving Word, Excel, PowerPoint, Outlook, or Teams may depend on your Microsoft subscription.",
  },
  {
    q: "How long does the course take?",
    a: "The course is organized into seven focused lessons that you can complete at your own pace.",
  },
  {
    q: "Will I receive a certificate?",
    a: "Yes. Students who complete the course receive a JK Academy certificate of completion.",
  },
  {
    q: "Is the price recurring?",
    a: "No. The course is offered as a $17 one-time purchase.",
  },
];

const scrollToCurriculum = () => {
  document.getElementById("curriculum")?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function CopilotPage() {
  useSeo({
    title: "7-Day Microsoft Copilot QuickStart | JK Academy",
    description:
      "Learn Microsoft Copilot for writing, Word, Outlook, Excel, PowerPoint, Teams, and everyday productivity in 7 practical lessons. $17 one-time purchase.",
  });

  return (
    <div data-testid="copilot-page" className="min-h-screen bg-[#030712] text-slate-100">
      <CourseHeader enrollUrl={COPILOT_URL} enrollLabel="Enroll $17" />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-grid opacity-60 animate-grid-pan" />
        <div className="absolute inset-0 -z-10 bg-radial-glow" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-slate-950/70 to-[#030712]" />

        <Container className="py-16 sm:py-24 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 border border-cyan-500/30 bg-cyan-500/5 px-3 py-1.5 mb-6">
                <CircleDot className="h-3.5 w-3.5 text-cyan-400 animate-pulse" />
                <Overline>JK Academy QuickStart Series</Overline>
              </div>
              <h1
                data-testid="copilot-hero-headline"
                className="font-display font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02]"
              >
                Master <span className="text-cyan-400 text-glow-cyan">Microsoft Copilot</span> in Just 7 Days
              </h1>
              <p className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed font-mono">
                Build practical AI skills for writing, communication, spreadsheets, presentations, meetings, and daily productivity — no previous AI experience needed.
              </p>

              <div className="mt-6 flex items-baseline gap-3">
                <span className="font-display font-black text-white text-4xl sm:text-5xl">$17</span>
                <span className="font-mono text-xs uppercase tracking-[0.22em] text-slate-400">
                  One-Time Purchase
                </span>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-start gap-4">
                <a
                  href={COPILOT_URL}
                  rel="noopener noreferrer"
                  data-testid="copilot-hero-cta-enroll"
                  className="group inline-flex items-center justify-center gap-3 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold font-mono uppercase tracking-[0.18em] text-sm px-8 py-4 shadow-[0_0_28px_rgba(6,182,212,0.45)] hover:shadow-[0_0_48px_rgba(6,182,212,0.75)] transition-all"
                >
                  <Sparkles className="h-4 w-4" />
                  Start the Copilot QuickStart
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
                <button
                  onClick={scrollToCurriculum}
                  data-testid="copilot-hero-cta-curriculum"
                  className="inline-flex items-center justify-center gap-3 bg-transparent hover:bg-cyan-500/10 text-cyan-300 font-bold font-mono uppercase tracking-[0.18em] text-sm px-8 py-4 border border-cyan-400/60 hover:border-cyan-300 transition-all"
                >
                  View the 7-Day Curriculum
                </button>
              </div>
            </div>

            {/* Course visual placeholder */}
            <div className="lg:col-span-5">
              <div className="relative bg-slate-900/60 backdrop-blur-md border border-cyan-500/30 aspect-[4/5] p-8 flex flex-col justify-between shadow-[0_0_60px_rgba(6,182,212,0.2)] overflow-hidden">
                <div className="absolute top-0 left-0 h-4 w-4 border-t border-l border-cyan-400" />
                <div className="absolute top-0 right-0 h-4 w-4 border-t border-r border-cyan-400" />
                <div className="absolute bottom-0 left-0 h-4 w-4 border-b border-l border-cyan-400" />
                <div className="absolute bottom-0 right-0 h-4 w-4 border-b border-r border-cyan-400" />
                <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-cyan-500/20 blur-3xl" />
                <div>
                  <Overline>7-Day QuickStart</Overline>
                  <div className="mt-3 font-display text-3xl sm:text-4xl font-black text-white leading-tight">
                    Copilot<br />QuickStart
                  </div>
                </div>
                <div>
                  <div className="grid grid-cols-4 gap-1.5 mb-4">
                    {[...Array(7)].map((_, i) => (
                      <div key={i} className="h-2 bg-cyan-500/60" />
                    ))}
                    <div className="h-2 border border-cyan-500/40" />
                  </div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-cyan-300 border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-center">
                    Beginner Friendly • Self-Paced • Certificate Included
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Benefits */}
      <section className="py-20 sm:py-28 border-t border-white/5">
        <Container>
          <Reveal>
            <Overline>// what you&apos;ll gain</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl">
              Practical wins in <span className="text-cyan-400">every workday</span>.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} delay={i * 0.06}>
                  <CardShell>
                    <div className="h-11 w-11 flex items-center justify-center border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 mb-5">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-display text-lg font-bold text-white">{b.title}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{b.desc}</p>
                  </CardShell>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Audience */}
      <section className="py-20 sm:py-28 border-t border-white/5">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14">
            <Reveal>
              <Overline>// who this is for</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
                Built for real <span className="text-cyan-400">work</span>.
              </h2>
              <p className="mt-5 font-mono text-sm text-slate-400 leading-relaxed max-w-md">
                Whether you&apos;re managing a team, running a small business, or just starting out — this course meets you at your level.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <CardShell>
                <BulletList items={AUDIENCE} testIdPrefix="copilot-audience-item" />
              </CardShell>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Curriculum */}
      <section id="curriculum" className="py-20 sm:py-28 border-t border-white/5">
        <Container>
          <Reveal>
            <Overline>// the curriculum</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl">
              7 focused days. <span className="text-cyan-400">One clear outcome.</span>
            </h2>
          </Reveal>

          <div
            data-testid="copilot-curriculum"
            className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6"
          >
            {CURRICULUM.map((stage, i) => {
              const Icon = stage.icon;
              return (
                <Reveal key={stage.stage} delay={i * 0.08}>
                  <div className="relative border border-white/10 bg-slate-900/40 p-7 h-full">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="h-10 w-10 flex items-center justify-center border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">
                        Stage 0{i + 1}
                      </span>
                    </div>
                    <h3 className="font-display text-xl font-bold text-white leading-tight">
                      {stage.stage}
                    </h3>
                    <ul className="mt-5 space-y-2.5">
                      {stage.lessons.map((l) => (
                        <li key={l} className="flex items-start gap-2.5 text-sm text-slate-300">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-cyan-400 shrink-0" />
                          <span className="font-mono">{l}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Student receives */}
      <section className="py-20 sm:py-28 border-t border-white/5">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14">
            <Reveal delay={0.1}>
              <CardShell>
                <div className="h-11 w-11 flex items-center justify-center border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 mb-5">
                  <Users className="h-5 w-5" />
                </div>
                <BulletList items={STUDENT_RECEIVES} />
              </CardShell>
            </Reveal>
            <Reveal>
              <Overline>// what you&apos;ll get</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
                Everything you need to <span className="text-cyan-400">ship</span>.
              </h2>
              <p className="mt-5 font-mono text-sm text-slate-400 leading-relaxed max-w-md">
                Prompts, examples, guidance, and a certificate — designed to give you real, portable Copilot skills.
              </p>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Certificate */}
      <section className="py-20 sm:py-28 border-t border-white/5">
        <Container>
          <Reveal>
            <div className="relative border border-cyan-500/40 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-10 sm:p-16 shadow-[0_0_80px_rgba(6,182,212,0.2)] overflow-hidden">
              <div className="absolute top-0 left-0 h-4 w-4 border-t border-l border-cyan-400" />
              <div className="absolute top-0 right-0 h-4 w-4 border-t border-r border-cyan-400" />
              <div className="absolute bottom-0 left-0 h-4 w-4 border-b border-l border-cyan-400" />
              <div className="absolute bottom-0 right-0 h-4 w-4 border-b border-r border-cyan-400" />
              <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center relative">
                <div>
                  <Overline>// certificate</Overline>
                  <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
                    Finish Strong with a <span className="text-cyan-400">JK Academy Certificate</span>.
                  </h2>
                  <p className="mt-5 font-mono text-sm text-slate-400 leading-relaxed">
                    Complete the course and receive a branded certificate recognizing your Microsoft Copilot learning achievement.
                  </p>
                  <a
                    href={COPILOT_URL}
                    rel="noopener noreferrer"
                    data-testid="copilot-certificate-cta"
                    className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold font-mono uppercase tracking-[0.18em] text-sm px-8 py-4 shadow-[0_0_28px_rgba(6,182,212,0.45)] hover:shadow-[0_0_48px_rgba(6,182,212,0.75)] transition-all"
                  >
                    <Sparkles className="h-4 w-4" />
                    Enroll for $17
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
                {/* Certificate mock */}
                <div className="relative aspect-[7/5] bg-slate-950 border border-cyan-500/50 p-6 sm:p-8 flex flex-col">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent" />
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-cyan-400">JK Academy</span>
                    <Award className="h-4 w-4 text-cyan-400" />
                  </div>
                  <div className="mt-auto mb-auto text-center">
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-400">
                      Certificate of Completion
                    </div>
                    <div className="mt-3 font-display text-lg sm:text-xl font-bold text-white leading-tight">
                      7-Day Microsoft<br />Copilot QuickStart
                    </div>
                  </div>
                  <div className="flex items-end justify-between">
                    <div className="border-t border-slate-600 pt-1 min-w-[80px]">
                      <div className="font-mono text-[8px] uppercase tracking-[0.22em] text-slate-500">Student</div>
                    </div>
                    <div className="border-t border-slate-600 pt-1 min-w-[80px] text-right">
                      <div className="font-mono text-[8px] uppercase tracking-[0.22em] text-slate-500">Instructor</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-28 border-t border-white/5">
        <Container>
          <Reveal>
            <Overline>// frequently asked</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl">
              Answers to <span className="text-cyan-400">common questions</span>.
            </h2>
          </Reveal>
          <div className="mt-12">
            <Faq items={FAQ} testIdPrefix="copilot-faq" />
          </div>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-28 border-t border-white/5 mb-20 sm:mb-0">
        <Container>
          <Reveal>
            <div className="relative border border-cyan-500/40 bg-slate-900/60 backdrop-blur-md p-10 sm:p-16 max-w-4xl mx-auto text-center shadow-[0_0_80px_rgba(6,182,212,0.2)]">
              <div className="absolute top-0 left-0 h-4 w-4 border-t border-l border-cyan-400" />
              <div className="absolute top-0 right-0 h-4 w-4 border-t border-r border-cyan-400" />
              <div className="absolute bottom-0 left-0 h-4 w-4 border-b border-l border-cyan-400" />
              <div className="absolute bottom-0 right-0 h-4 w-4 border-b border-r border-cyan-400" />

              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
                Your Microsoft Copilot Journey <span className="text-cyan-400 text-glow-cyan">Starts Today</span>.
              </h2>
              <p className="mt-5 max-w-xl mx-auto font-mono text-sm sm:text-base text-slate-400 leading-relaxed">
                Spend one week building AI skills that can save time, strengthen your work, and improve everyday productivity.
              </p>
              <a
                href={COPILOT_URL}
                rel="noopener noreferrer"
                data-testid="copilot-final-cta"
                className="mt-10 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold font-mono uppercase tracking-[0.18em] text-sm px-10 py-5 shadow-[0_0_28px_rgba(6,182,212,0.45)] hover:shadow-[0_0_48px_rgba(6,182,212,0.75)] transition-all"
              >
                <Sparkles className="h-4 w-4" />
                Start Learning for $17
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </Container>
      </section>

      <CourseFooter />
    </div>
  );
}
